import { __read, __spread } from "tslib";
var NIL_NODE = { lvl: 0 };
function newAANode(k, v, lvl, l, r) {
    if (l === void 0) { l = NIL_NODE; }
    if (r === void 0) { r = NIL_NODE; }
    return { k: k, v: v, lvl: lvl, l: l, r: r };
}
export function empty(node) {
    return node === NIL_NODE;
}
export function newTree() {
    return NIL_NODE;
}
export function remove(node, key) {
    if (empty(node))
        return NIL_NODE;
    var k = node.k, l = node.l, r = node.r;
    if (key === k) {
        if (empty(l)) {
            return r;
        }
        if (empty(r)) {
            return l;
        }
        var _a = __read(last(l), 2), lastKey = _a[0], lastValue = _a[1];
        return adjust(clone(node, { k: lastKey, v: lastValue, l: deleteLast(l) }));
    }
    if (key < k) {
        return adjust(clone(node, { l: remove(l, key) }));
    }
    return adjust(clone(node, { r: remove(r, key) }));
}
export function find(node, key) {
    if (empty(node)) {
        return;
    }
    if (key === node.k) {
        return node.v;
    }
    if (key < node.k) {
        return find(node.l, key);
    }
    return find(node.r, key);
}
export function findMaxKeyValue(node, value, field) {
    if (field === void 0) { field = 'k'; }
    if (empty(node)) {
        return [-Infinity, undefined];
    }
    if (node[field] === value) {
        return [node.k, node.v];
    }
    if (node[field] < value) {
        var r = findMaxKeyValue(node.r, value, field);
        if (r[0] === -Infinity) {
            return [node.k, node.v];
        }
        return r;
    }
    return findMaxKeyValue(node.l, value, field);
}
export function insert(node, k, v) {
    if (empty(node)) {
        return newAANode(k, v, 1);
    }
    if (k === node.k) {
        return clone(node, { k: k, v: v });
    }
    if (k < node.k) {
        return rebalance(clone(node, { l: insert(node.l, k, v) }));
    }
    return rebalance(clone(node, { r: insert(node.r, k, v) }));
}
export function walkWithin(node, start, end) {
    if (empty(node)) {
        return [];
    }
    var k = node.k, v = node.v, l = node.l, r = node.r;
    var result = [];
    if (k > start) {
        result = result.concat(walkWithin(l, start, end));
    }
    if (k >= start && k <= end) {
        result.push({ k: k, v: v });
    }
    if (k <= end) {
        result = result.concat(walkWithin(r, start, end));
    }
    return result;
}
export function walk(node) {
    if (empty(node)) {
        return [];
    }
    return __spread(walk(node.l), [{ k: node.k, v: node.v }], walk(node.r));
}
function last(node) {
    return empty(node.r) ? [node.k, node.v] : last(node.r);
}
function deleteLast(node) {
    return empty(node.r)
        ? node.l
        : adjust(clone(node, { r: deleteLast(node.r) }));
}
function clone(node, args) {
    return newAANode(args.k !== undefined ? args.k : node.k, args.v !== undefined ? args.v : node.v, args.lvl !== undefined ? args.lvl : node.lvl, args.l !== undefined ? args.l : node.l, args.r !== undefined ? args.r : node.r);
}
function isSingle(node) {
    return empty(node) || node.lvl > node.r.lvl;
}
function rebalance(node) {
    return split(skew(node));
}
function adjust(node) {
    var l = node.l, r = node.r, lvl = node.lvl;
    if (r.lvl >= lvl - 1 && l.lvl >= lvl - 1) {
        return node;
    }
    if (lvl > r.lvl + 1) {
        if (isSingle(l)) {
            return skew(clone(node, { lvl: lvl - 1 }));
        }
        if (!empty(l) && !empty(l.r)) {
            return clone(l.r, {
                l: clone(l, { r: l.r.l }),
                r: clone(node, {
                    l: l.r.r,
                    lvl: lvl - 1,
                }),
                lvl: lvl,
            });
        }
        throw new Error('Unexpected empty nodes');
    }
    else if (isSingle(node)) {
        return split(clone(node, { lvl: lvl - 1 }));
    }
    else if (!empty(r) && !empty(r.l)) {
        var rl = r.l;
        var rlvl = isSingle(rl) ? r.lvl - 1 : r.lvl;
        return clone(rl, {
            l: clone(node, {
                r: rl.l,
                lvl: lvl - 1,
            }),
            r: split(clone(r, { l: rl.r, lvl: rlvl })),
            lvl: rl.lvl + 1,
        });
    }
    else {
        throw new Error('Unexpected empty nodes');
    }
}
export function keys(node) {
    if (empty(node)) {
        return [];
    }
    return __spread(keys(node.l), [node.k], keys(node.r));
}
export function ranges(node) {
    return toRanges(walk(node));
}
export function rangesWithin(node, startIndex, endIndex) {
    if (empty(node)) {
        return [];
    }
    var adjustedStart = findMaxKeyValue(node, startIndex)[0];
    return toRanges(walkWithin(node, adjustedStart, endIndex));
}
export function arrayToRanges(items, parser) {
    var length = items.length;
    if (length === 0) {
        return [];
    }
    var _a = parser(items[0]), start = _a.index, value = _a.value;
    var result = [];
    for (var i = 1; i < length; i++) {
        var _b = parser(items[i]), nextIndex = _b.index, nextValue = _b.value;
        result.push({ start: start, end: nextIndex - 1, value: value });
        start = nextIndex;
        value = nextValue;
    }
    result.push({ start: start, end: Infinity, value: value });
    return result;
}
function toRanges(nodes) {
    return arrayToRanges(nodes, function (_a) {
        var index = _a.k, value = _a.v;
        return ({ index: index, value: value });
    });
}
function split(node) {
    var r = node.r, lvl = node.lvl;
    return !empty(r) && !empty(r.r) && r.lvl === lvl && r.r.lvl === lvl
        ? clone(r, { l: clone(node, { r: r.l }), lvl: lvl + 1 })
        : node;
}
function skew(node) {
    var l = node.l;
    return !empty(l) && l.lvl === node.lvl
        ? clone(l, { r: clone(node, { l: l.r }) })
        : node;
}
